import type { DownloadJobDocuments } from '../generated/models/download-job-documents';
import type { DocumentItem as RawDocumentItem } from '../generated/models/document-item';
import type { WorkOrderDocument as RawWorkOrderDocument } from '../generated/models/work-order-document';
import type { DocumentDownloadResponse, DocumentItem, WorkOrderDocument } from '../../models/document.model';

/** Raw API shape for affidavit_document entries (not in generated types). */
interface RawAffidavitDocument {
  document_id?: number;
  document_name?: string;
  document_code?: string;
  document_source?: string;
}

/**
 * v2-extended request body — adds optional `document_type`/`document_format`
 * to the generated v1 shape. `document_type: ['MERGED']` instructs the v2 API
 * to return a server-side ZIP archive instead of individual base64-encoded
 * documents. `document_format` is forwarded as-is by the backend to the
 * external manner-preview API (`App\Services\MannerPreviewService`) used to
 * build `affidavit_document` (AFF) — it is *not* restricted to a fixed set of
 * values server-side, but this app always sends `'pdf'` on every AFF
 * fetch, regardless of job type — see `AffidavitComponent.loadContent()`. See
 * `openapispec.yml`'s `serverDocumentsDownloadV2` description for the full
 * backend contract.
 */
type DownloadJobDocumentsV2 = DownloadJobDocuments & { document_type?: string[]; document_format?: string };

/**
 * Builds the request body for
 * `POST /process-server/{serverId}/documents/download` (v2).
 *
 * @param codes          - Document codes to download (e.g. `['AFF']`, `['SD', 'WO']`).
 * @param jobNums        - Job numbers associated with those documents.
 * @param documentType   - Optional v2-only `document_type` values (e.g. `['MERGED']`
 *                         to request a server-side ZIP). Omit for standard JSON response.
 * @param documentFormat - Optional v2-only `document_format` value (e.g. `'pdf'`) —
 *                         only meaningful when requesting `'AFF'`; controls what format
 *                         the backend's manner-preview API renders the affidavit in.
 *                         Omit to get the backend's default (`'html'`). The one real
 *                         caller (`AffidavitContentService.fetch()`) always passes `'pdf'`.
 */
export function toPayload(
  codes: string[],
  jobNums: number[],
  documentType?: string[],
  documentFormat?: string,
): DownloadJobDocumentsV2 {
  const payload: DownloadJobDocumentsV2 = { document_code: codes, job_number: jobNums };
  if (documentType?.length) {
    payload.document_type = documentType;
  }
  if (documentFormat) {
    payload.document_format = documentFormat;
  }
  return payload;
}

/**
 * Maps the `POST /process-server/{serverId}/documents/download` response
 * (v1 and v2).
 *
 * @remarks
 * v1 response: `{ "data": [{ "job_number", "document_copy", "service_documents": [],
 *   "workorder_documents": [{ "document_id", "document_name", "document_code",
 *   "document_source" }] }] }`.
 *
 * v2 adds `document_mime_type` on every document object and populates
 * `service_documents` (was always `[]` in v1).
 *
 * @param raw - Untyped API response.
 */
export function documentDownloadToModel(raw: unknown): DocumentDownloadResponse {
  const r = raw as { data?: RawDocumentItem[] };
  return {
    data: (r?.data ?? []).map(mapDocumentItem),
  };
}

function mapDocumentItem(item: RawDocumentItem & { affidavit_document?: RawAffidavitDocument[] }): DocumentItem {
  return {
    jobNumber:          item.job_number ?? 0,
    documentCopy:       item.document_copy ?? 0,
    serviceDocuments:   (item.service_documents ?? []).map(mapWorkOrderDocument),
    workorderDocuments: (item.workorder_documents ?? []).map(mapWorkOrderDocument),
    affidavitDocuments: (item.affidavit_document ?? []).map(mapWorkOrderDocument),
  };
}

function mapWorkOrderDocument(
  doc: RawWorkOrderDocument & { document_message?: string; document_mime_type?: string },
): WorkOrderDocument {
  return {
    documentId:       doc.document_id       ?? 0,
    documentName:     doc.document_name     ?? '',
    documentCode:     doc.document_code     ?? '',
    documentSource:   doc.document_source   ?? '',
    documentMessage:  doc.document_message  ?? '',
    documentMimeType: doc.document_mime_type,
  };
}

/**
 * Decodes a base64 string to a raw binary byte array.
 *
 * @remarks
 * Shared by {@link decodeMergedDocument} (ZIP/PDF download) and
 * `AffidavitContentService` (inline PDF detection/rendering) so both paths
 * use one `atob` → `Uint8Array` implementation instead of duplicating it.
 *
 * @param base64 - Base64-encoded content (no `data:` URI prefix).
 * @returns Decoded bytes.
 * @throws If `base64` is not valid base64.
 */
export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/** Chunk size for {@link bytesToBase64}'s `String.fromCharCode` calls — keeps each call's argument count well under engines' call-stack-argument limits. */
const BYTES_TO_BASE64_CHUNK_SIZE = 8192;

/**
 * Encodes a raw binary byte array to a base64 string — the inverse of
 * {@link base64ToBytes}.
 *
 * @remarks
 * Used to prepare a filled PDF's bytes (see `PdfFillService`) for encryption
 * and submission the same way `AffidavitComponent` already base64-encodes the
 * patched HTML string before encrypting it. Converts in chunks rather than a
 * single `String.fromCharCode(...bytes)` spread — a PDF-sized byte array can
 * exceed the argument-count limit engines impose on spread/apply calls.
 *
 * @param bytes - Raw bytes to encode.
 * @returns Base64-encoded string (no `data:` URI prefix).
 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binaryString = '';
  for (let i = 0; i < bytes.length; i += BYTES_TO_BASE64_CHUNK_SIZE) {
    const chunk = bytes.subarray(i, i + BYTES_TO_BASE64_CHUNK_SIZE);
    binaryString += String.fromCharCode(...chunk);
  }
  return btoa(binaryString);
}

/**
 * Decodes a base64-encoded merged document (ZIP or PDF) to a binary Blob.
 *
 * @remarks
 * When `document_type: ['MERGED']` is requested, the API returns `{ data: [{ document_source }] }`
 * where `document_source` is base64-encoded ZIP (for ZIP mode) or merged PDF (for DIRECT mode).
 * This function decodes the base64 string to a binary Blob for download.
 *
 * @param base64Source - Base64-encoded document content from the API response.
 * @param mimeType     - MIME type for the blob (e.g. `'application/zip'`).
 * @returns Binary Blob ready for download.
 * @throws If the base64 string is invalid or the conversion fails.
 */
export function decodeMergedDocument(base64Source: string, mimeType = 'application/zip'): Blob {
  try {
    return new Blob([base64ToBytes(base64Source)], { type: mimeType });
  } catch (err) {
    throw new Error(`Failed to decode base64 document: ${err instanceof Error ? err.message : 'unknown error'}`);
  }
}
